// src/components/clinica/TarjetaRecordatorios.js
// =========================================================================
// RECORDATORIOS DE CITA POR CORREO (solo admin)
//
// Interruptor de la clínica. Con los recordatorios activos, el paciente que
// tiene correo recibe un aviso el día antes con botones para confirmar o
// anular la cita (backend: services/recordatorios.service.js).
// =========================================================================
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

export default function TarjetaRecordatorios({ numero, activosIniciales = false }) {
    const { showToast } = useToast();
    const [activos, setActivos] = useState(Boolean(activosIniciales));
    const [guardando, setGuardando] = useState(false);

    const cambiar = async () => {
        const nuevo = !activos;
        setGuardando(true);
        try {
            const res = await api.put('/clinica/recordatorios', { activos: nuevo });
            setActivos(Boolean(res.activos));
            showToast(res.activos ? 'Recordatorios activados.' : 'Recordatorios desactivados.', 'success');
        } catch (err) {
            showToast(err.message || 'No se pudo guardar.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <section className="bg-white border border-slate-200 rounded-[2rem] p-6 md:p-8 shadow-sm space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-black text-slate-900">{numero ? `${numero}. ` : ''}Recordatorios de cita por correo</h2>
                    <p className="text-xs font-semibold text-slate-500 mt-1 max-w-2xl">
                        El día antes de la cita, el paciente recibe un correo con dos botones: «Confirmo que voy» y «No puedo ir».
                        Si la anula, el hueco queda libre en la agenda. Solo llega a los pacientes que tienen correo en su ficha.
                    </p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={activos}
                    aria-label="Recordatorios de cita por correo"
                    onClick={cambiar}
                    disabled={guardando}
                    className={`relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-50 ${activos ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                    <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${activos ? 'translate-x-6' : ''}`} />
                </button>
            </div>

            <div className={`rounded-2xl px-4 py-3 text-sm font-semibold ${activos ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-50 text-slate-600'}`}>
                {activos
                    ? 'Activados. Se envían entre las 8:00 y las 22:00, unas 24 horas antes de cada cita. En la agenda verás «Confirmada» o «Aviso enviado».'
                    : 'Desactivados. No se envía ningún correo a los pacientes.'}
            </div>

            <p className="text-xs font-semibold text-slate-500">
                El correo incluye la dirección y el teléfono de la clínica que están en{' '}
                <Link to="/admin/datos-fiscales" className="text-clinica-primary hover:underline">Datos fiscales</Link>.
                Si el paciente responde al correo, la respuesta llega al correo de la clínica.
            </p>
        </section>
    );
}
